import { usePdfAsImage } from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/usePdfAsImage'
import {
  cantileverDocumentEntryId as deriveCantileverDocumentEntryId,
  cantileverDocumentPageIdForPage,
} from 'features/knowledgeDocumentViewer/utils/cantileverHostIds'
import {
  resolvePageImageFileRef,
  resolvePdfFileRef,
} from 'features/knowledgeDocumentViewer/utils/documentPageImages'
import { useRolodexSignedURLs } from 'features/rolodex'
import { useMemo } from 'react'

interface UseDocumentPageImageOptions {
  cantileverDocumentPageId?: string
  cantileverDocumentEntryId?: string
  siteId?: string
  bqDocumentId?: string
  filename?: string
  skipHiRes?: boolean
  pageNumber?: number
}

interface UseDocumentPageImageResult {
  thumbnailUrl: string | undefined
  isThumbLoading: boolean
  hiResUrl: string | null
  isHiResLoading: boolean
  refreshThumbnail: () => Promise<void>
}

/**
 * Thumbnail and 6400px hosts are UUID5-derived from site + BQ document id.
 * Indexed `data.page_id` / `data.document_id` loads are for metadata, not
 * these image files.
 */
export const useDocumentPageImage = ({
  cantileverDocumentPageId,
  cantileverDocumentEntryId,
  siteId,
  bqDocumentId,
  filename,
  skipHiRes = false,
  pageNumber = 1,
}: UseDocumentPageImageOptions): UseDocumentPageImageResult => {
  const derivedCantileverPageId = useMemo(() => {
    if (!siteId || !bqDocumentId) return undefined
    return cantileverDocumentPageIdForPage(siteId, bqDocumentId, pageNumber)
  }, [bqDocumentId, pageNumber, siteId])

  const primaryCantileverPageId =
    cantileverDocumentPageId ?? derivedCantileverPageId

  const derivedCantileverDocumentEntryId = useMemo(() => {
    if (!siteId || !bqDocumentId) return undefined
    return deriveCantileverDocumentEntryId(siteId, bqDocumentId)
  }, [bqDocumentId, siteId])

  const primaryCantileverDocumentEntryId =
    cantileverDocumentEntryId ?? derivedCantileverDocumentEntryId

  const primaryThumbQuery = useMemo(() => {
    if (!primaryCantileverPageId) return undefined
    return resolvePageImageFileRef(primaryCantileverPageId, 'thumbnail')
  }, [primaryCantileverPageId])

  const {
    url: primaryThumbUrl,
    isLoading: isPrimaryThumbLoading,
    forceRefresh: refreshThumbnail,
  } = useRolodexSignedURLs(primaryThumbQuery)

  const hiResPrimaryQuery = useMemo(() => {
    if (skipHiRes || !primaryCantileverPageId) return undefined
    return resolvePageImageFileRef(primaryCantileverPageId, 'hiRes')
  }, [primaryCantileverPageId, skipHiRes])

  const { url: hiResPrimaryUrl, isLoading: isHiResPrimaryLoading } =
    useRolodexSignedURLs(hiResPrimaryQuery)

  const thumbnailUrl = primaryThumbUrl
  const isThumbUrlLoading = isPrimaryThumbLoading

  const hiResUrl = skipHiRes ? null : (hiResPrimaryUrl ?? null)
  const isHiResUrlLoading = skipHiRes ? false : isHiResPrimaryLoading

  const thumbnail404 = !isThumbUrlLoading && !thumbnailUrl
  const hiRes404 = !skipHiRes && !isHiResUrlLoading && !hiResUrl
  const pdfFallbackNeeded =
    !skipHiRes &&
    !!filename &&
    !!primaryCantileverDocumentEntryId &&
    (thumbnail404 || hiRes404)

  const pdfQuery = useMemo(
    () =>
      pdfFallbackNeeded && filename && primaryCantileverDocumentEntryId
        ? resolvePdfFileRef(primaryCantileverDocumentEntryId, filename)
        : undefined,
    [filename, pdfFallbackNeeded, primaryCantileverDocumentEntryId]
  )
  const { url: pdfSignedUrl, isLoading: isPdfUrlLoading } =
    useRolodexSignedURLs(pdfQuery)
  const { imageUrl: pdfImageUrl, isLoading: isPdfConvertLoading } =
    usePdfAsImage(
      pdfSignedUrl,
      pageNumber,
      skipHiRes ? 0.5 : 1,
      1,
      true,
      skipHiRes
        ? {
            preloadAdjacentPages: false,
            releaseDocumentAfterRender: true,
          }
        : undefined
    )
  const isPdfLoading = isPdfUrlLoading || isPdfConvertLoading

  return {
    thumbnailUrl: thumbnailUrl ?? pdfImageUrl ?? undefined,
    isThumbLoading:
      isThumbUrlLoading || (thumbnail404 && pdfFallbackNeeded && isPdfLoading),
    hiResUrl: skipHiRes ? null : (hiResUrl ?? pdfImageUrl ?? null),
    isHiResLoading: skipHiRes
      ? false
      : isHiResUrlLoading || (hiRes404 && pdfFallbackNeeded && isPdfLoading),
    refreshThumbnail,
  }
}
